import React from 'react';
import { View, Text, ScrollView, ActivityIndicator, RefreshControl, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Animated, { FadeInDown } from 'react-native-reanimated';
import {
  Users2,
  Activity,
  Zap,
  BarChart3,
  ShieldX,
  Smartphone,
  Globe,
  Monitor,
  AlertCircle,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAdminRole, hasPermission, ADMIN_ROLE_LABELS } from '@/lib/admin';
import { useExecutiveDashboard } from '@/lib/admin/use-executive-dashboard';
import type { ExecutiveDashboardData, ExecutiveKPIs, SideBalance } from '@/lib/admin/use-executive-dashboard';
import { isWeb } from '@/lib/platform';
import { AppLayout } from '@/components/AppLayout';
import { WebCard, WebGrid } from '@/components/web/WebAppShell';
import { StatCardEnhanced } from '@/components/admin/StatCardEnhanced';
import { AreaChart } from '@/components/admin/AreaChart';
import { FunnelChart } from '@/components/admin/FunnelChart';
import { CohortHeatmap } from '@/components/admin/CohortHeatmap';

export default function ExecutiveDashboardScreen() {
  const colors = useThemeColors();
  const { data: adminRole, isLoading: roleLoading } = useAdminRole();
  const {
    data,
    isLoading,
    isError,
    isRefetching,
    refetch,
  } = useExecutiveDashboard();

  // Loading
  if (roleLoading || isLoading) {
    return (
      <AppLayout
        title="Analytics"
        breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Analytics' }]}
      >
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      </AppLayout>
    );
  }

  // Permission gate
  if (!hasPermission(adminRole, 'dashboard_engagement')) {
    return (
      <AppLayout
        title="Analytics"
        breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Analytics' }]}
      >
        <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.secondary }}>
          <ShieldX size={48} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-4 text-center">
            Access Restricted
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm text-center mt-2">
            Your admin role does not have permission to view executive analytics.
          </Text>
        </View>
      </AppLayout>
    );
  }

  // Failed query must not render as a zero-filled dashboard ("0 users" reads as
  // real data). Surface the error + retry instead.
  if (isError || !data) {
    return (
      <AppLayout
        title="Analytics"
        breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Analytics' }]}
      >
        <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.secondary }}>
          <AlertCircle size={32} color={colors.error} />
          <Text style={{ color: colors.text.primary }} className="text-base font-semibold mt-3 text-center">
            Couldn&apos;t load analytics
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1 mb-4 text-center">
            The KPI query failed. This is not the same as zero activity.
          </Text>
          <Pressable
            onPress={() => refetch()}
            className="px-5 py-2.5 rounded-xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text style={{ color: '#fff' }} className="font-semibold">Retry</Text>
          </Pressable>
        </View>
      </AppLayout>
    );
  }

  const kpis = data?.kpis;
  const activationColor = (kpis?.activation_rate ?? 0) >= 60 ? '#22C55E' : (kpis?.activation_rate ?? 0) >= 40 ? '#F59E0B' : '#EF4444';

  // Prepare area chart data
  const growthData = (data?.userGrowth ?? []).map((row) => ({
    date: row.date,
    values: [row.new_startups, row.new_investors],
  }));

  const balance = data?.sideBalance;
  const totalSides = (balance?.total_startups ?? 0) + (balance?.total_investors ?? 0);
  const startupPct = totalSides > 0 ? ((balance?.total_startups ?? 0) / totalSides) * 100 : 50;

  // Feature engagement: max for bar width
  const maxEventsPerUser = Math.max(...(data?.featureEngagement ?? []).map((f) => f.events_per_user), 1);

  if (!isWeb) {
    return (
      <MobileExecutiveDashboard
        data={data}
        isRefetching={isRefetching}
        refetch={refetch}
        kpis={kpis}
        activationColor={activationColor}
        growthData={growthData}
        balance={balance}
        startupPct={startupPct}
        maxEventsPerUser={maxEventsPerUser}
        colors={colors}
      />
    );
  }

  return (
    <AppLayout
      title="Analytics"
      breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Analytics' }]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['bottom']}>
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 40 }}
          refreshControl={
            <RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} tintColor={colors.primary[600]} />
          }
        >
          {/* Section 1: KPI Cards */}
          <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
            <WebGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap={16}>
              <StatCardEnhanced
                icon={<Users2 size={22} color={colors.primary[600]} />}
                iconBg={`${colors.primary[600]}12`}
                label="Total Users"
                value={kpis?.total_users ?? 0}
              />
              <StatCardEnhanced
                icon={<Activity size={22} color="#007AFF" />}
                iconBg="#007AFF12"
                label="MAU"
                value={kpis?.mau ?? 0}
              />
              <StatCardEnhanced
                icon={<Zap size={22} color={activationColor} />}
                iconBg={`${activationColor}12`}
                label="Activation Rate"
                value={`${(kpis?.activation_rate ?? 0).toFixed(1)}%`}
              />
              <StatCardEnhanced
                icon={<BarChart3 size={22} color="#8B5CF6" />}
                iconBg="#8B5CF612"
                label="Avg Inverio"
                value={(kpis?.avg_inverio_score ?? 0).toFixed(1)}
              />
            </WebGrid>
          </Animated.View>

          {/* Section 2: User Growth */}
          <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader
                title="User Growth"
                subtitle="New signups over 90 days"
                colors={colors}
              />
              <AreaChart
                data={growthData}
                colors={['#0D9488', '#3B82F6']}
                labels={['Startups', 'Investors']}
                height={220}
              />
            </WebCard>
          </Animated.View>

          {/* Section 3: Dual Funnels */}
          <Animated.View entering={FadeInDown.delay(300).duration(500)} className="mb-6">
            <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
              <WebCard>
                <SectionHeader
                  title="Startup Funnel"
                  subtitle="Onboarding progression"
                  colors={colors}
                />
                <FunnelChart stages={data?.startupFunnel ?? []} />
              </WebCard>
              <WebCard>
                <SectionHeader
                  title="Investor Funnel"
                  subtitle="Activation journey"
                  colors={colors}
                />
                <FunnelChart stages={data?.investorFunnel ?? []} />
              </WebCard>
            </WebGrid>
          </Animated.View>

          {/* Section 4: Retention Cohorts */}
          <Animated.View entering={FadeInDown.delay(400).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader
                title="Retention Cohorts"
                subtitle="Weekly retention by signup cohort"
                colors={colors}
              />
              <CohortHeatmap cohorts={data?.retentionCohorts ?? []} />
            </WebCard>
          </Animated.View>

          {/* Section 5: Marketplace Balance */}
          <Animated.View entering={FadeInDown.delay(450).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader
                title="Marketplace Balance"
                subtitle={`Startup:Investor ratio ${balance?.ratio?.toFixed(2) ?? '—'}:1`}
                colors={colors}
              />
              {/* Balance bar */}
              <View className="h-8 rounded-full overflow-hidden flex-row" style={{ backgroundColor: colors.surface.secondary }}>
                <View
                  className="h-full justify-center items-center"
                  style={{ width: `${startupPct}%`, backgroundColor: '#0D948830' }}
                >
                  <Text className="text-[10px] font-bold" style={{ color: '#0D9488' }}>
                    {balance?.total_startups ?? 0} startups
                  </Text>
                </View>
                <View
                  className="h-full justify-center items-center flex-1"
                  style={{ backgroundColor: '#3B82F630' }}
                >
                  <Text className="text-[10px] font-bold" style={{ color: '#3B82F6' }}>
                    {balance?.total_investors ?? 0} investors
                  </Text>
                </View>
              </View>
              {/* Active counts */}
              <View className="flex-row justify-between mt-3">
                <View className="flex-row items-center gap-1.5">
                  <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#0D9488' }} />
                  <Text style={{ color: colors.text.secondary }} className="text-xs">
                    {balance?.active_startups ?? 0} active startups
                  </Text>
                </View>
                <View className="flex-row items-center gap-1.5">
                  <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
                  <Text style={{ color: colors.text.secondary }} className="text-xs">
                    {balance?.active_investors ?? 0} active investors
                  </Text>
                </View>
              </View>
            </WebCard>
          </Animated.View>

          {/* Section 6: Matching Funnel */}
          <Animated.View entering={FadeInDown.delay(500).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader
                title="Matching Funnel"
                subtitle="Match lifecycle stages"
                colors={colors}
              />
              <FunnelChart stages={data?.matchingFunnel ?? []} />
            </WebCard>
          </Animated.View>

          {/* Section 7: Feature Engagement */}
          <Animated.View entering={FadeInDown.delay(550).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader
                title="Feature Engagement"
                subtitle="Events per user (top 8 features)"
                colors={colors}
              />
              <View className="gap-2.5">
                {(data?.featureEngagement ?? []).map((feat) => {
                  const barPct = (feat.events_per_user / maxEventsPerUser) * 100;
                  return (
                    <View key={feat.feature_name} className="flex-row items-center gap-3">
                      <Text
                        style={{ color: colors.text.secondary, width: 100 }}
                        className="text-xs font-medium"
                        numberOfLines={1}
                      >
                        {feat.feature_name}
                      </Text>
                      <View className="flex-1 h-5 rounded-md overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                        <View
                          className="h-full rounded-md"
                          style={{
                            width: `${barPct}%`,
                            backgroundColor: '#8B5CF630',
                          }}
                        />
                      </View>
                      <Text
                        style={{ color: colors.text.primary, width: 36 }}
                        className="text-xs font-bold text-right"
                      >
                        {feat.events_per_user.toFixed(1)}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </WebCard>
          </Animated.View>

          {/* Section 8: Session Analytics */}
          <Animated.View entering={FadeInDown.delay(600).duration(500)}>
            <WebCard>
              <SectionHeader
                title="Session Analytics"
                subtitle="By platform"
                colors={colors}
              />
              {/* Table header */}
              <View className="flex-row items-center py-2 mb-1" style={{ borderBottomWidth: 1, borderBottomColor: colors.border.light }}>
                <Text style={{ color: colors.text.tertiary, flex: 1.5 }} className="text-[10px] font-semibold uppercase">
                  Platform
                </Text>
                <Text style={{ color: colors.text.tertiary, flex: 1, textAlign: 'right' }} className="text-[10px] font-semibold uppercase">
                  Sessions
                </Text>
                <Text style={{ color: colors.text.tertiary, flex: 1, textAlign: 'right' }} className="text-[10px] font-semibold uppercase">
                  Avg Duration
                </Text>
                <Text style={{ color: colors.text.tertiary, flex: 1, textAlign: 'right' }} className="text-[10px] font-semibold uppercase">
                  Events/Sess
                </Text>
              </View>
              {/* Table rows */}
              {(data?.sessionAnalytics ?? []).map((row) => {
                const PlatformIcon = row.platform === 'iOS' ? Smartphone : row.platform === 'Android' ? Smartphone : row.platform === 'Web' ? Globe : Monitor;
                return (
                  <View
                    key={row.platform}
                    className="flex-row items-center py-2.5"
                    style={{ borderBottomWidth: 1, borderBottomColor: colors.border.light }}
                  >
                    <View className="flex-row items-center gap-2" style={{ flex: 1.5 }}>
                      <PlatformIcon size={14} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.primary }} className="text-xs font-medium" numberOfLines={1}>
                        {row.platform}
                      </Text>
                    </View>
                    <Text style={{ color: colors.text.primary, flex: 1, textAlign: 'right' }} className="text-xs font-semibold">
                      {row.total_sessions.toLocaleString()}
                    </Text>
                    <Text style={{ color: colors.text.secondary, flex: 1, textAlign: 'right' }} className="text-xs">
                      {row.avg_duration_min.toFixed(1)}m
                    </Text>
                    <Text style={{ color: colors.text.secondary, flex: 1, textAlign: 'right' }} className="text-xs">
                      {row.events_per_session.toFixed(1)}
                    </Text>
                  </View>
                );
              })}
            </WebCard>
          </Animated.View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}

// ---- Mobile layout ----

type MobileExecutiveDashboardProps = {
  data: ExecutiveDashboardData | undefined;
  isRefetching: boolean;
  refetch: () => void;
  kpis: ExecutiveKPIs | undefined;
  activationColor: string;
  growthData: { date: string; values: number[] }[];
  balance: SideBalance | undefined;
  startupPct: number;
  maxEventsPerUser: number;
  colors: ReturnType<typeof useThemeColors>;
};

function MobileExecutiveDashboard({
  data,
  isRefetching,
  refetch,
  kpis,
  activationColor,
  growthData,
  balance,
  startupPct,
  maxEventsPerUser,
  colors,
}: MobileExecutiveDashboardProps) {
  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['top', 'bottom']}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 40, paddingHorizontal: 16 }}
        refreshControl={
          <RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} tintColor={colors.primary[600]} />
        }
      >
        {/* KPIs: 2x2 compact pill grid */}
        <Animated.View entering={FadeInDown.delay(80).duration(400)} className="mb-4">
          <View className="flex-row flex-wrap gap-3">
            <MobileKpiPill
              icon={<Users2 size={14} color={colors.primary[600]} />}
              label="Total Users"
              value={String(kpis?.total_users ?? 0)}
              accentColor={colors.primary[600]}
              colors={colors}
            />
            <MobileKpiPill
              icon={<Activity size={14} color="#007AFF" />}
              label="MAU"
              value={String(kpis?.mau ?? 0)}
              accentColor="#007AFF"
              colors={colors}
            />
            <MobileKpiPill
              icon={<Zap size={14} color={activationColor} />}
              label="Activation"
              value={`${(kpis?.activation_rate ?? 0).toFixed(1)}%`}
              accentColor={activationColor}
              colors={colors}
            />
            <MobileKpiPill
              icon={<BarChart3 size={14} color="#8B5CF6" />}
              label="Avg Inverio"
              value={(kpis?.avg_inverio_score ?? 0).toFixed(1)}
              accentColor="#8B5CF6"
              colors={colors}
            />
          </View>
        </Animated.View>

        {/* User Growth chart */}
        <Animated.View entering={FadeInDown.delay(140).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="User Growth" subtitle="New signups over 90 days" colors={colors} />
            <AreaChart
              data={growthData}
              colors={['#0D9488', '#3B82F6']}
              labels={['Startups', 'Investors']}
              height={180}
            />
          </MobileCard>
        </Animated.View>

        {/* Funnels: stacked 1 column */}
        <Animated.View entering={FadeInDown.delay(200).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="Startup Funnel" subtitle="Onboarding progression" colors={colors} />
            <FunnelChart stages={data?.startupFunnel ?? []} />
          </MobileCard>
        </Animated.View>
        <Animated.View entering={FadeInDown.delay(240).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="Investor Funnel" subtitle="Activation journey" colors={colors} />
            <FunnelChart stages={data?.investorFunnel ?? []} />
          </MobileCard>
        </Animated.View>

        {/* Retention cohorts */}
        <Animated.View entering={FadeInDown.delay(280).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="Retention Cohorts" subtitle="Weekly retention by signup cohort" colors={colors} />
            <CohortHeatmap cohorts={data?.retentionCohorts ?? []} />
          </MobileCard>
        </Animated.View>

        {/* Marketplace Balance */}
        <Animated.View entering={FadeInDown.delay(320).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader
              title="Marketplace Balance"
              subtitle={`Startup:Investor ratio ${balance?.ratio?.toFixed(2) ?? '—'}:1`}
              colors={colors}
            />
            <View className="h-7 rounded-full overflow-hidden flex-row" style={{ backgroundColor: colors.surface.secondary }}>
              <View
                className="h-full justify-center items-center"
                style={{ width: `${startupPct}%`, backgroundColor: '#0D948830' }}
              >
                <Text className="text-[11px] font-bold" style={{ color: '#0D9488' }} numberOfLines={1}>
                  {balance?.total_startups ?? 0} startups
                </Text>
              </View>
              <View
                className="h-full justify-center items-center flex-1"
                style={{ backgroundColor: '#3B82F630' }}
              >
                <Text className="text-[11px] font-bold" style={{ color: '#3B82F6' }} numberOfLines={1}>
                  {balance?.total_investors ?? 0} investors
                </Text>
              </View>
            </View>
            <View className="flex-row justify-between mt-2">
              <View className="flex-row items-center gap-1.5">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#0D9488' }} />
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  {balance?.active_startups ?? 0} active startups
                </Text>
              </View>
              <View className="flex-row items-center gap-1.5">
                <View className="w-2 h-2 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  {balance?.active_investors ?? 0} active investors
                </Text>
              </View>
            </View>
          </MobileCard>
        </Animated.View>

        {/* Matching Funnel */}
        <Animated.View entering={FadeInDown.delay(360).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="Matching Funnel" subtitle="Match lifecycle stages" colors={colors} />
            <FunnelChart stages={data?.matchingFunnel ?? []} />
          </MobileCard>
        </Animated.View>

        {/* Feature Engagement: top 6 */}
        <Animated.View entering={FadeInDown.delay(400).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="Feature Engagement" subtitle="Events per user (top 6 features)" colors={colors} />
            <View className="gap-2">
              {(data?.featureEngagement ?? []).slice(0, 6).map((feat) => {
                const barPct = (feat.events_per_user / maxEventsPerUser) * 100;
                return (
                  <View key={feat.feature_name} className="flex-row items-center gap-2">
                    <Text
                      style={{ color: colors.text.secondary, width: 88 }}
                      className="text-xs font-medium"
                      numberOfLines={1}
                    >
                      {feat.feature_name}
                    </Text>
                    <View className="flex-1 h-4 rounded-md overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                      <View
                        className="h-full rounded-md"
                        style={{ width: `${barPct}%`, backgroundColor: '#8B5CF630' }}
                      />
                    </View>
                    <Text
                      style={{ color: colors.text.primary, width: 32 }}
                      className="text-xs font-bold text-right"
                    >
                      {feat.events_per_user.toFixed(1)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </MobileCard>
        </Animated.View>

        {/* Session Analytics: vertical card list */}
        <Animated.View entering={FadeInDown.delay(440).duration(400)} className="mb-4">
          <MobileCard colors={colors}>
            <MobileSectionHeader title="Session Analytics" subtitle="By platform" colors={colors} />
            <View className="gap-3">
              {(data?.sessionAnalytics ?? []).map((row) => {
                const PlatformIcon = row.platform === 'iOS' ? Smartphone : row.platform === 'Android' ? Smartphone : row.platform === 'Web' ? Globe : Monitor;
                return (
                  <View
                    key={row.platform}
                    className="rounded-xl px-3 py-2.5"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <View className="flex-row items-center gap-2 mb-2">
                      <PlatformIcon size={14} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.primary }} className="text-sm font-semibold">
                        {row.platform}
                      </Text>
                    </View>
                    <View className="gap-1">
                      <View className="flex-row justify-between">
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">Sessions</Text>
                        <Text style={{ color: colors.text.primary }} className="text-xs font-bold">
                          {row.total_sessions.toLocaleString()}
                        </Text>
                      </View>
                      <View className="flex-row justify-between">
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">Avg Duration</Text>
                        <Text style={{ color: colors.text.secondary }} className="text-xs">
                          {row.avg_duration_min.toFixed(1)}m
                        </Text>
                      </View>
                      <View className="flex-row justify-between">
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">Events/Session</Text>
                        <Text style={{ color: colors.text.secondary }} className="text-xs">
                          {row.events_per_session.toFixed(1)}
                        </Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </MobileCard>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
}

function MobileCard({
  children,
  colors,
}: {
  children: React.ReactNode;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="rounded-2xl px-4 py-4 overflow-hidden"
      style={{ backgroundColor: colors.surface.primary }}
    >
      {children}
    </View>
  );
}

function MobileKpiPill({
  icon,
  label,
  value,
  accentColor,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accentColor: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="flex-1 rounded-2xl px-3 py-3 gap-1"
      style={{ backgroundColor: colors.surface.primary, minWidth: '45%' }}
    >
      <View className="flex-row items-center gap-1.5">
        {icon}
        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
          {label}
        </Text>
      </View>
      <Text style={{ color: accentColor }} className="text-xl font-bold" numberOfLines={1}>
        {value}
      </Text>
    </View>
  );
}

function MobileSectionHeader({
  title,
  subtitle,
  colors,
}: {
  title: string;
  subtitle: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="mb-3">
      <Text style={{ color: colors.text.primary }} className="text-base font-bold">
        {title}
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5" numberOfLines={2}>
        {subtitle}
      </Text>
    </View>
  );
}

// ---- Web helpers ----

function SectionHeader({
  title,
  subtitle,
  colors,
}: {
  title: string;
  subtitle: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="mb-4">
      <Text style={{ color: colors.text.primary }} className="text-base font-bold">
        {title}
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5">
        {subtitle}
      </Text>
    </View>
  );
}
